import React, { useState, useEffect } from 'react';

const API_BASE = process.env.REACT_APP_API_URL + '/api';

export default function OfficerNotificationModal({ 
  isOpen, 
  onClose, 
  selectedAssessments, 
  assessments, 
  onSuccess 
}) {
  const [officers, setOfficers] = useState([]);
  const [selectedOfficers, setSelectedOfficers] = useState([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchOfficers();
    }
  }, [isOpen]);

  const fetchOfficers = async () => {
    try {
      const res = await fetch(`${API_BASE}/risk-assessment/officers`, {
        credentials: 'include'
      });
      const data = await res.json();
      if (data.success) {
        setOfficers(data.officers);
      } else {
        setError('Failed to load officers');
      }
    } catch (err) {
      setError('Error loading officers');
      console.error(err);
    }
  };

  const handleOfficerSelect = (officerId, isSelected) => {
    if (isSelected) {
      setSelectedOfficers(prev => [...prev, officerId]);
    } else {
      setSelectedOfficers(prev => prev.filter(id => id !== officerId));
    }
  };

  const handleSelectAllOfficers = () => {
    if (selectedOfficers.length === officers.length) {
      setSelectedOfficers([]);
    } else {
      setSelectedOfficers(officers.map(o => o.id));
    }
  };

  const handleSendNotifications = async () => {
    if (selectedOfficers.length === 0) {
      setError('Please select at least one officer');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API_BASE}/risk-assessment/notify-officers`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          assessment_ids: selectedAssessments,
          officer_ids: selectedOfficers,
          message: message.trim() || null
        })
      });

      const data = await res.json();
      if (data.success) {
        onSuccess(data.message);
        onClose();
        // Reset form
        setSelectedOfficers([]);
        setMessage('');
      } else {
        setError(data.message || 'Failed to send notifications');
      }
    } catch (err) {
      setError('Error sending notifications');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const selectedAssessmentTitles = assessments
    .filter(a => selectedAssessments.includes(a.id))
    .map(a => a.title);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              Notify Officers
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-6 max-h-[calc(90vh-140px)] overflow-y-auto">
          {/* Selected Assessments */}
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-2">
              Selected Assessments ({selectedAssessments.length})
            </h4>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <ul className="text-sm text-blue-900 space-y-1">
                {selectedAssessmentTitles.map((title, index) => (
                  <li key={index} className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    {title}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Officers Selection */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-gray-900">
                Select Officers ({officers.length} available)
              </h4>
              <button
                onClick={handleSelectAllOfficers}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                {selectedOfficers.length === officers.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            
            <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
              {officers.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  No officers found
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {officers.map((officer) => (
                    <label
                      key={officer.id}
                      className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedOfficers.includes(officer.id)}
                        onChange={(e) => handleOfficerSelect(officer.id, e.target.checked)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">
                          {officer.name}
                        </div>
                        <div className="text-xs text-gray-500">
                          {officer.email}
                          {officer.department_name && ` • ${officer.department_name}`}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Custom Message */}
          <div>
            <label htmlFor="message" className="block text-sm font-medium text-gray-900 mb-2">
              Custom Message (Optional)
            </label>
            <textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Add a custom message to include with the notification..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-sm"
              rows={3}
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span className="text-sm text-red-800">{error}</span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSendNotifications}
              disabled={loading || selectedOfficers.length === 0}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Sending...
                </div>
              ) : (
                `Send to ${selectedOfficers.length} Officer${selectedOfficers.length !== 1 ? 's' : ''}`
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
